/**
 * Pure helpers for inline HTML-style emphasis tags (`<b>`, `<strong>`,
 * `<i>`, `<em>`).
 *
 * History (PLT-4806): the chat surface deliberately ships a tiny
 * HTML-like inline directive set — `<badge>`, `<cite>`, `<private />`.
 * Janne (in the PLT-4806 thread) asked that "for any formatting tags
 * that have obvious pure-html matches, probably best to use the same
 * html-style naming everywhere — so `<b>` for bold etc." Without that,
 * the model has to switch between two grammars within a single answer
 * (HTML for `<badge>`/`<cite>` and Markdown for `**bold**`), and the
 * mix is exactly the kind of cognitive load that drives drift like
 * `<cite index="…">…</cite>`.
 *
 * react-markdown does NOT render raw HTML by default — `html` mdast
 * nodes are dropped — and the project has no `rehype-raw` dependency,
 * so we cannot just turn the option on. Instead we follow the same
 * pattern as the other inline directives: a remark plugin walks `text`
 * nodes, splits them on `<b>`/`<strong>` (and italic siblings), and
 * replaces each match with a real mdast `strong` / `emphasis` node.
 * Markdown `**bold**` and `*italic*` keep working unchanged because
 * they're already turned into `strong` / `emphasis` upstream by
 * `remarkParse`.
 *
 * Marker grammar (canonical):
 *
 *   `<b>text</b>`           — bold
 *   `<strong>text</strong>` — bold (synonym)
 *   `<i>text</i>`           — italic
 *   `<em>text</em>`         — italic (synonym)
 *
 * Lax forms — match the rest of the chat directive parser:
 *
 *   `<B>text</B>`           — case-insensitive
 *   `<b >text</b >`         — whitespace tolerant
 *
 * Self-closing forms (`<b />`) drop to nothing — they carry no content.
 *
 * Pure module — no AST mutation, no `unist-util-visit` dependency — so
 * unit tests can drive it directly under mocha+ts-node, matching the
 * other `*-markers.ts` modules in this folder.
 */
import { findTagsInText, type ParsedTag } from './html-tag-parser';

/** Mdast `text` node shape (subset). */
export interface EmphasisTextNode {
  type: 'text';
  value: string;
}

/** Mdast `strong` node minted for `<b>` / `<strong>` (subset). */
export interface EmphasisStrongNode {
  type: 'strong';
  children: EmphasisTextNode[];
}

/** Mdast `emphasis` node minted for `<i>` / `<em>` (subset). */
export interface EmphasisEmphasisNode {
  type: 'emphasis';
  children: EmphasisTextNode[];
}

export type EmphasisSplitChild = EmphasisTextNode | EmphasisStrongNode | EmphasisEmphasisNode;

const STRONG_TAG_NAMES = ['b', 'strong'] as const;
const EMPHASIS_TAG_NAMES = ['i', 'em'] as const;

/**
 * An emphasis tag tokenised by micromark as its OWN `html` node value.
 *
 * PLT-5054: this is the common production case the `text`-node splitter
 * above never sees. When the model emits a paired `<b>bold</b>`,
 * `remarkParse`/micromark splits it into three sibling nodes —
 * `html('<b>')`, `text('bold')`, `html('</b>')` — because `<b>` is a
 * recognised raw-HTML inline tag. `splitTextOnEmphasisMarkers` only runs
 * against single `text` strings, so it matches none of the three (the
 * lone opener has no closer). The `html`-node fragments then leak raw to
 * the DOM because we don't run `rehype-raw`. Same root cause as PLT-4896
 * / PLT-4945 for `<cite>`. The reconstruction below stitches the split
 * siblings back into a `strong` / `emphasis` node.
 *
 * Anchored (`^…$`) — an html node value for an emphasis tag is exactly
 * the tag itself (no attributes in our grammar), modulo case and
 * whitespace, which we tolerate to match the rest of the lax parser.
 */
const HTML_OPEN_RE = /^<(b|strong|i|em)\s*>$/i;
const HTML_CLOSE_RE = /^<\/(b|strong|i|em)\s*>$/i;
const HTML_SELF_CLOSE_RE = /^<(b|strong|i|em)\s*\/>$/i;

/** Map a tag name to the mdast node type it mints (b/strong → strong, i/em → emphasis). */
function emitForTagName(tagName: string): 'strong' | 'emphasis' {
  const lower = tagName.toLowerCase();
  return lower === 'b' || lower === 'strong' ? 'strong' : 'emphasis';
}

/**
 * Minimal mdast node shape the reconstruction needs to inspect: a `type`,
 * an optional `value` (html / text nodes), and optional `children`
 * (container nodes + the `strong` / `emphasis` nodes we mint).
 */
export interface EmphasisReconNode {
  type: string;
  value?: string;
  children?: EmphasisReconNode[];
}

/**
 * Reconstruct `<b>` / `<strong>` / `<i>` / `<em>` emphasis spans that
 * micromark split across sibling `html` / `text` nodes (PLT-5054).
 *
 * Walks one parent's `children` array left-to-right. When an `html` node
 * is an opening emphasis tag, scans forward for the matching closing
 * `html` node of the same tag name (tracking nesting depth of same-name
 * tags), then replaces the open…close range with a single `strong` /
 * `emphasis` node whose children are the nodes in between — recursing
 * into that inner slice so nested emphasis (`<b>a <i>b</i></b>`) is
 * handled too. Self-closing forms (`<b/>`) and empty paired forms
 * (`<b></b>`) collapse to nothing, matching `splitTextOnEmphasisMarkers`.
 *
 * Returns `null` when nothing changed so the remark plugin can skip the
 * splice. An unclosed opener is left untouched (no matching close found),
 * preserving the existing malformed-tag contract — the syntax-leak
 * detector can still flag it.
 *
 * Pure function (no AST mutation, generic over the node shape) so it can
 * be unit-tested directly, matching the rest of this folder.
 */
export function reconstructEmphasisFromHtmlNodes(children: EmphasisReconNode[]): EmphasisReconNode[] | null {
  let changed = false;
  const out: EmphasisReconNode[] = [];
  let i = 0;
  while (i < children.length) {
    const node = children[i];
    if (node.type === 'html' && typeof node.value === 'string') {
      // Self-closing `<b />` carries no content — drop it.
      if (HTML_SELF_CLOSE_RE.test(node.value)) {
        changed = true;
        i += 1;
        continue;
      }
      const open = HTML_OPEN_RE.exec(node.value);
      if (open) {
        const openName = open[1].toLowerCase();
        // Find the matching closer among the following siblings, tracking
        // nesting of same-name openers so `<b><b>x</b></b>` pairs the
        // outer tags correctly. Synonyms (b/strong, i/em) are matched by
        // exact name, mirroring `findTagsInText`'s same-name closing rule.
        let depth = 1;
        let closeIdx = -1;
        for (let j = i + 1; j < children.length; j++) {
          const sibling = children[j];
          if (sibling.type !== 'html' || typeof sibling.value !== 'string') continue;
          const innerOpen = HTML_OPEN_RE.exec(sibling.value);
          if (innerOpen && innerOpen[1].toLowerCase() === openName) {
            depth += 1;
            continue;
          }
          const innerClose = HTML_CLOSE_RE.exec(sibling.value);
          if (innerClose && innerClose[1].toLowerCase() === openName) {
            depth -= 1;
            if (depth === 0) {
              closeIdx = j;
              break;
            }
          }
        }
        if (closeIdx !== -1) {
          const innerRaw = children.slice(i + 1, closeIdx);
          const innerProcessed = reconstructEmphasisFromHtmlNodes(innerRaw) ?? innerRaw;
          // Empty paired form (`<b></b>`) collapses to nothing.
          if (innerProcessed.length > 0) {
            out.push({ type: emitForTagName(openName), children: innerProcessed });
          }
          changed = true;
          i = closeIdx + 1;
          continue;
        }
        // No matching closer — leave the opener untouched (unclosed contract).
      }
    }
    out.push(node);
    i += 1;
  }
  return changed ? out : null;
}

interface AnnotatedTag extends ParsedTag {
  /** Mdast node type to emit for this tag. */
  emit: 'strong' | 'emphasis';
}

/**
 * Find every `<b>` / `<strong>` / `<i>` / `<em>` occurrence in `text`,
 * sorted by source position. Self-closing forms are included with empty
 * inner text — the splitter drops them so the rendered output reads
 * naturally.
 */
function findEmphasisTags(text: string): AnnotatedTag[] {
  const tags: AnnotatedTag[] = [];
  for (const name of STRONG_TAG_NAMES) {
    for (const t of findTagsInText(text, name)) {
      tags.push({ ...t, emit: 'strong' });
    }
  }
  for (const name of EMPHASIS_TAG_NAMES) {
    for (const t of findTagsInText(text, name)) {
      tags.push({ ...t, emit: 'emphasis' });
    }
  }
  // Sort by start position. Overlap is not expected (matched tags by
  // their full open/close spans), and the splitter below tracks
  // `lastEnd` defensively to skip any remaining overlap if it ever
  // occurs.
  tags.sort((a, b) => a.start - b.start);
  return tags;
}

/**
 * Split a text string on inline emphasis tags and return the resulting
 * mdast children — text nodes for the spans between tags, `strong` /
 * `emphasis` nodes for each well-formed paired tag.
 *
 * Returns `null` when the string contains no emphasis tags so callers
 * (the remark plugin) can short-circuit without splicing the parent's
 * children. Self-closing forms are dropped (they carry no inner text).
 *
 * Pure function — no AST mutation, no dependency on `unist-util-visit` —
 * so unit tests can drive it directly.
 */
export function splitTextOnEmphasisMarkers(text: string): EmphasisSplitChild[] | null {
  const tags = findEmphasisTags(text);
  if (tags.length === 0) return null;

  const newChildren: EmphasisSplitChild[] = [];
  let lastEnd = 0;
  for (const tag of tags) {
    // Defensive: skip an overlap (shouldn't happen with paired-tag
    // matches, but keeps the splitter total even if the parser ever
    // returns nested paired tags).
    if (tag.start < lastEnd) continue;
    if (tag.start > lastEnd) {
      newChildren.push({ type: 'text', value: text.slice(lastEnd, tag.start) });
    }
    if (!tag.selfClosing && tag.innerText.length > 0) {
      newChildren.push({
        type: tag.emit,
        children: [{ type: 'text', value: tag.innerText }],
      });
    }
    // Self-closing forms (`<b />`) and empty paired forms (`<b></b>`)
    // collapse to nothing — there is no content to render.
    lastEnd = tag.end;
  }
  if (lastEnd < text.length) {
    newChildren.push({ type: 'text', value: text.slice(lastEnd) });
  }
  return newChildren;
}
